/**
 * Note HTML helpers — shared by the rich note editor and every place a note
 * body renders (notes list, activity timeline, reveal view).
 *
 * SECURITY MODEL: rich notes are sanitized server-side at write time
 * (lead-notes.service), but we ALSO sanitize at render with this strict
 * allowlist. Two reasons:
 *   1. Legacy notes were stored as raw text — if one happens to contain
 *      angle brackets, rendering it as HTML unsanitized would be XSS.
 *   2. Defense in depth — the render path never trusts stored content.
 */

const ALLOWED_TAGS = new Set([
  "B", "STRONG", "I", "EM", "U", "S", "STRIKE", "P", "BR", "DIV",
  "UL", "OL", "LI", "SPAN", "MARK", "BLOCKQUOTE", "HR", "A", "IMG",
]);

const ALLOWED_STYLES = new Set(["color", "background-color", "font-size"]);

/**
 * Inline note images may only come from OUR Supabase storage. The URL is
 * resolved inline (no cross-module import — a stale cached chunk of an
 * imported module turned the previous helper import into "not a function"
 * and crashed every note render). Handles the obfuscated NEXT_PUBLIC_SB_U
 * form (reversed base64 — same deobfuscation supabaseClient/supabaseAuthService
 * use); plain NEXT_PUBLIC_SUPABASE_URL is unset in this app.
 */
let imgPrefixCache: string | null = null;
const imgSrcPrefix = (): string => {
  if (imgPrefixCache !== null) return imgPrefixCache;
  try {
    let url = process.env.NEXT_PUBLIC_SUPABASE_URL || "";
    if (!url) {
      const obfuscated = (process.env.NEXT_PUBLIC_SB_U || "").replace(/^["']|["']$/g, "");
      if (obfuscated) url = atob(obfuscated.split("").reverse().join(""));
    }
    imgPrefixCache = url ? `${url.replace(/\/$/, "")}/storage/v1/object/public/` : "";
  } catch {
    imgPrefixCache = "";
  }
  return imgPrefixCache;
};

const isSafeHref = (href: string): boolean => /^https?:\/\//i.test(href);

/** Heuristic: does this stored note contain HTML formatting? */
export const noteIsHtml = (s: string | null | undefined): boolean =>
  !!s && /<(b|strong|i|em|u|s|strike|p|br|div|ul|ol|li|span|mark|blockquote|hr|a|img)[\s/>]/i.test(s);

/**
 * Strict allowlist sanitizer. Parses with DOMParser (inert document — scripts
 * never execute during parsing) and rebuilds only allowed tags/styles.
 * Everything else — event handlers, urls, iframes, classes, ids — is dropped.
 */
export function sanitizeNoteHtml(html: string): string {
  if (typeof window === "undefined") return "";
  const doc = new DOMParser().parseFromString(html, "text/html");

  const cleanNode = (node: Node, out: Node, outDoc: Document) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        out.appendChild(outDoc.createTextNode(child.textContent || ""));
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return;
      const el = child as HTMLElement;
      if (!ALLOWED_TAGS.has(el.tagName)) {
        // Disallowed wrapper (font, a, script, img...) — keep its TEXT content
        // by recursing into it without creating the element.
        cleanNode(el, out, outDoc);
        return;
      }
      // Links: http(s) only, forced new-tab + no opener. Drop the wrapper
      // (keep its text) when the href is unsafe.
      if (el.tagName === "A") {
        const href = el.getAttribute("href") || "";
        if (!isSafeHref(href)) {
          cleanNode(el, out, outDoc);
          return;
        }
        const a = outDoc.createElement("a");
        a.setAttribute("href", href);
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer nofollow");
        cleanNode(el, a, outDoc);
        out.appendChild(a);
        return;
      }
      // Images: only our storage URLs — anything else is dropped entirely.
      if (el.tagName === "IMG") {
        const src = el.getAttribute("src") || "";
        const prefix = imgSrcPrefix();
        if (!prefix || !src.startsWith(prefix)) return;
        const img = outDoc.createElement("img");
        img.setAttribute("src", src);
        const alt = el.getAttribute("alt");
        if (alt) img.setAttribute("alt", alt.slice(0, 200));
        // Numeric sizing from the editor's drag-resize (display size only)
        const width = el.getAttribute("width");
        if (width && /^\d{2,4}$/.test(width)) img.setAttribute("width", width);
        out.appendChild(img);
        return;
      }
      const clean = outDoc.createElement(el.tagName.toLowerCase());
      // style: copy only color/background-color
      const styleParts: string[] = [];
      ALLOWED_STYLES.forEach((prop) => {
        const v = el.style.getPropertyValue(prop);
        // only simple color values — hex, rgb(a), named
        if (v && /^[#a-z0-9(),.\s%-]+$/i.test(v)) styleParts.push(`${prop}: ${v}`);
      });
      if (styleParts.length) clean.setAttribute("style", styleParts.join("; "));
      // mention metadata survives (used for display + potential re-parse)
      const mid = el.getAttribute("data-mention-id");
      const mname = el.getAttribute("data-mention-name");
      if (mid && /^[a-f0-9-]{10,40}$/i.test(mid)) clean.setAttribute("data-mention-id", mid);
      if (mname) clean.setAttribute("data-mention-name", mname.slice(0, 80));
      cleanNode(el, clean, outDoc);
      out.appendChild(clean);
    });
  };

  const outDoc = document.implementation.createHTMLDocument("");
  const container = outDoc.createElement("div");
  cleanNode(doc.body, container, outDoc);
  return container.innerHTML;
}

/** Storage-hosted image URLs inside a note (for collapsed-row thumbnails). */
export function noteImageSources(s: string | null | undefined): string[] {
  if (!s || typeof window === "undefined" || !s.includes("<img")) return [];
  const prefix = imgSrcPrefix();
  if (!prefix) return [];
  const doc = new DOMParser().parseFromString(s, "text/html");
  return Array.from(doc.body.querySelectorAll("img"))
    .map((img) => img.getAttribute("src") || "")
    .filter((src) => src.startsWith(prefix));
}

/** Plain text of a note regardless of storage format (for search, previews). */
export function noteToPlainText(s: string | null | undefined): string {
  if (!s) return "";
  if (!noteIsHtml(s)) return s;
  if (typeof window === "undefined") return s.replace(/<[^>]+>/g, " ");
  const doc = new DOMParser().parseFromString(s, "text/html");
  return doc.body.textContent || "";
}
